import { Injectable, signal, computed, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '@awo-care/shared-auth';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private router = inject(Router);

  private currentRoute = signal<string>('');
  private routeHistory = signal<string[]>([]);

  readonly showBackButton = computed(() => {
    const route = this.currentRoute();
    // Don't show back button on root, empty route, or /care (main dashboard)
    return route !== '' && route !== '/' && route !== '/care';
  });

  constructor() {
    // Initialize with current route on service creation
    this.currentRoute.set(this.router.url);
    this.routeHistory.set([this.router.url]);

    // Subscribe to router events to update current route
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute.set(event.urlAfterRedirects);

        // Update route history
        const currentHistory = this.routeHistory();
        const newHistory = [...currentHistory, event.urlAfterRedirects];
        this.routeHistory.set(newHistory);
      });
  }

  goBack(): void {
    const currentUrl = this.currentRoute();

    if (currentUrl === '' || currentUrl === '/') {
      return;
    }
    // For auth pages (login/register), navigate to the main website
    if (
      ['/auth/login', '/auth/register'].some((segment) =>
        currentUrl.includes(segment)
      )
    ) {
      window.location.href = environment.appUrls.website;
      return;
    }
    // For profile, navigate to care dashboard
    if (currentUrl.includes('/profile')) {
      void this.router.navigate(['/care']);
      return;
    }
    if (currentUrl.includes('/care/holiday-care/vacation-periods/')) {
      void this.router.navigate(['/care/holiday-care']);
      return;
    }
    // Handle legal pages - navigate back to /care for logged-in users
    if (currentUrl.includes('/legal/cookies') || currentUrl.includes('/legal/imprint')) {
      void this.router.navigate(['/care']);
      return;
    }

    const urlSegments = currentUrl.split('/').filter((segment) => segment);

    // Handle routes with parameters (like /care/details/:id)
    if (urlSegments.length >= 3) {
      const lastSegment = urlSegments[urlSegments.length - 1];
      //   const secondLastSegment = urlSegments[urlSegments.length - 2];

      // If the last segment looks like a parameter (ID)
      if (this.isParameterValue(lastSegment)) {
        // Navigate to the parent path (remove the parameter and its parent segment)
        const parentPath = urlSegments.slice(0, -2);
        void this.router.navigate(['/' + parentPath.join('/')]);
        return;
      }
    }

    // Default behavior for other routes
    if (urlSegments.length > 1) {
      const newUrl = '/' + urlSegments.slice(0, -1).join('/');
      void this.router.navigate([newUrl]);
    } else {
      void this.router.navigate(['/']);
    }
  }

  private isParameterValue(segment: string): boolean {
    // Check for UUID pattern
    const uuidPattern =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    // Check for Firebase ID pattern (20 characters)
    const firebaseIdPattern = /^[0-9a-zA-Z]{20}$/;
    // Check for general ID pattern (numbers only)
    const numberPattern = /^\d+$/;
    // Check for MongoDB ObjectId pattern (24 hex characters)
    const objectIdPattern = /^[0-9a-fA-F]{24}$/;

    return (
      uuidPattern.test(segment) ||
      firebaseIdPattern.test(segment) ||
      numberPattern.test(segment) ||
      objectIdPattern.test(segment)
    );
  }
}
